import dayjs, { Dayjs } from "dayjs";
import moment, { Moment, unitOfTime } from "moment";
import customToast from "../components/shared/services/toaster-service";
import { findErrorMessage } from "./metadata";
import { getLabel } from "../hooks/use-labels";
import { SelectOptionProps, UploadedFiles } from "./common-types";
import { AccountProps } from "../containers/Main/Dashboard/RecentTransactions/RTForm/rt-form-types";
import { RequiredTextProps } from "components/FormFactory/form-factory-types";
import { resetFormFieldValues } from "hooks/form-helpers";
import { getSubDivisionsLookup } from "apis/b2c_api";

type CodeNameType = { name: string; code: string };
const PNF = require("google-libphonenumber").PhoneNumberFormat;

export const phoneInstance = () => {
  return require("google-libphonenumber").PhoneNumberUtil.getInstance();
};
export const getPlanePhoneNumber = (countryDialingCode: string, phoneNumber: string) => {
  return `+${countryDialingCode}${phoneNumber}`;
};

export const showErrorNotification = (error: any) => {
  if (!error) {
    customToast.error(findErrorMessage("GENERAL", "en") || "Something went wrong!");
    return;
  }

  if (typeof error === "string") {
    customToast.error(error);
    return;
  }

  const messageArray: string[] = [];
  const fieldErrors = error?.field_errors || error?.field_error;

  if (fieldErrors?.length > 0) {
    for (const fieldError of fieldErrors) {
      if (fieldError.details) {
        messageArray.push(capitalize(fieldError.details));
      } else if (fieldError.message) {
        messageArray.push(capitalize(fieldError.message));
      }
    }
  }

  if (messageArray.length > 0) {
    messageArray.forEach((msg) => customToast.error(msg));
    return;
  }

  let message = error?.message || error?.title;

  if (!message) {
    const errorCode = error?.error_code || error?.response_code;
    if (errorCode) {
      message = findErrorMessage(errorCode, "en");
    }
  }

  if (!message) {
    message = findErrorMessage("GENERAL", "en") || "Something went wrong!";
  }

  customToast.error(message);
};

export const isJSON = (val: string): boolean => {
  if (typeof val === "string" && val.length > 1) {
    const startChar = val[0];
    const endChar = val[val.length - 1];
    return ["{", "["].includes(startChar) && ["]", "}"].includes(endChar);
  }

  return false;
};

export const timeDiff = (from: any, to: any, unit: unitOfTime.Diff = "seconds"): number => {
  return moment(from).diff(to, unit);
};

export const timeDiffFromNow = (time: Moment, unit = "seconds"): number => {
  return timeDiff(time, moment(), unit as unitOfTime.Diff);
};

export const padStart = (value: string, length: number, character: string) => {
  return value.padStart(length, character);
};

export const getCardLastFour = (cardId: string, cards?: Array<any>) => {
  let lastFour: string | undefined | null = undefined;
  if (cards && cards?.length > 0) {
    lastFour = cards?.find((card: any) => card?.id === cardId)?.lastFour;
  }
  if (!lastFour) return undefined;
  return padLastFour(lastFour);
};

export const addSpaceInCardNumber = (cardNumber: string) => {
  return cardNumber.match(/.{1,4}/g)?.join(" ");
};

export const padLastFourHtmlTag = (lastFour: string) => {
  return lastFour ? lastFour.padStart(14, "<span>....<span>") : "";
};

export const padLastFour = (lastFour: string) => {
  return lastFour ? lastFour.padStart(9, "....") : "";
};

export const padLastFive = (lastFive: string) => {
  return lastFive ? lastFive.padStart(23, "<span>.....</span>") : "";
};

export const formatCurrency = (amount: number, currency: string) => {
  if (!currency || currency.toLowerCase() === "null") {
    return amount ?? "-";
  }

  const safeAmount = amount ?? 0;
  const upperCurrency = currency.toUpperCase();

  try {
    const formatter = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: upperCurrency,
      currencyDisplay: "code",
    });
    return formatter.format(safeAmount);
  } catch (error) {
    const formatted = new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 6,
    }).format(safeAmount);

    return `${upperCurrency} ${formatted}`;
  }
};

export const formatAmountOnly = (amount: number): string => {
  let formattedAmount = "0.00";
  if (amount) {
    const formatter = new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    formattedAmount = formatter.format(amount);
  }
  return formattedAmount;
};
export const capitalize = (word: string) => {
  return word[0].toUpperCase() + word.slice(1).toLowerCase();
};
export const startOfWeek = () => {
  return dayjs().startOf("week");
};

export const endOfWeek = () => {
  return dayjs().endOf("week");
};

export const lastWeek = () => {
  return [dayjs().add(-7, "d"), dayjs()];
};

export const lastMonth = (): [Dayjs, Dayjs] => {
  return [dayjs().add(-30, "d"), dayjs()];
};

export const accountLastFour = (accountProxyNo: any) => {
  return accountProxyNo?.toString()?.substring(accountProxyNo.toString().length - 4);
};

export const accountLastFive = (accountProxyNo: any) => {
  return accountProxyNo?.toString()?.substring(accountProxyNo.toString().length - 5);
};
export const getSecureURLDetails = () => {
  return localStorage.getItem("Cloud_SecureURL");
};
export const getStorageItem = (key: string): any => {
  const item = localStorage.getItem(key);

  if (item) return JSON.parse(item);

  return null;
};
export const removeStorageItemList = (storage: string[]) => {
  storage.forEach((storageItem: string) => {
    localStorage.removeItem(storageItem);
  });
};

export const capitalizeTextTransform = (string: string): string => {
  return string[0].toUpperCase() + string.slice(1).toLowerCase();
};

export const getBrandIdName = (): string => {
  const brandProps = getStorageItem("init");
  if (isEmpty(brandProps)) {
    const { auth_app_id } = brandProps;
    return auth_app_id.toLowerCase();
  }

  return "consumer";
};

export const checkImageSrc = (key: string, type?: string): string => {
  let assetPath = "/assets/images";
  let imgSrc: string;

  if (type === "FLAG") {
    assetPath = "/assets/country-flags";
  }

  if (getLabel(key) && getLabel(key).includes("http")) {
    imgSrc = getLabel(key);
  } else if (getLabel(key)) {
    imgSrc = `${assetPath}/${getLabel(key)}`;
  } else {
    imgSrc = isImage(key) ? `${assetPath}/${key}` : "";
  }
  return imgSrc;
};

const isImage = (url: string) => {
  return /\.(jpg|jpeg|png|webp|avif|gif|svg|ico)$/.test(url);
};

export const maskAndShowLastChars = (
  str: string,
  showCharLen: number,
  characterReplaceLength: number,
) => {
  if (str.length <= showCharLen) {
    return str;
  }

  const dots = ".".repeat(characterReplaceLength);
  const lastFourChars = str.slice(-showCharLen);

  return dots + lastFourChars;
};

export const setLookupsOnStorage = (lookups: any[]) => {
  lookups.forEach((lookup) => {
    const { lookupCode, lookupTable } = lookup;
    if (Array.isArray(lookupTable)) {
      const lookupList = lookupTable.map((item: any) => {
        const { name, code, description } = item;
        return lookupCode === "document-types" ? { name, code, description } : { name, code };
      });
      const lookupTypes = lookupTable.map((item: any) => {
        const { code, description } = item;
        return lookupCode === "document-types" ? description : code;
      });
      localStorage.setItem(lookupCode, JSON.stringify(lookupList));
      localStorage.setItem(`${lookupCode}-list`, JSON.stringify(lookupTypes));
    } else {
      localStorage.removeItem(lookupCode);
      localStorage.removeItem(`${lookupCode}-list`);
    }
  });
};

export const getOptionsForSelectField = (lookupKey: string): SelectOptionProps[] => {
  const lookupData = getStorageItem(lookupKey) || [];

  return lookupData.map((type: any) => ({
    key: type.code,
    value: type.code,
    label: type.name,
  }));
};

export const santizeInvalidCharacter = (event: React.KeyboardEvent<HTMLInputElement>) => {
  if (event.key === "+" || event.key === "-" || event.key === "e" || event.key === "E") {
    event.preventDefault();
  }
};

export const downloadFileMethod = async (
  fileData: BlobPart,
  fileName: string,
  fileType: string,
) => {
  if (!fileData) return;
  try {
    const blob = new Blob([fileData], { type: fileType });
    const url = window.URL.createObjectURL(blob);
    // Create an anchor element and trigger the download
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Revoke the Object URL to free memory
    window.URL.revokeObjectURL(url);
  } catch (error) {
    showErrorNotification(error);
  }
};

export const getDescriptionFromLookup = (lookup: string, code: string) => {
  const lookupRes: Array<CodeNameType> = getStorageItem(lookup) || [];
  return lookupRes?.find((item: any) => item.code === code)?.name || code;
};

export const getLastFourDigits = (ddaNumber: string): string => {
  if (!ddaNumber) return "";
  return ddaNumber.slice(-4);
};

export const checkAddressFieldsValue = (formValue: any, requiredFieldsKeys: string[]) => {
  for (const key of requiredFieldsKeys) {
    if (!formValue[key]) {
      return false;
    }
  }
  return true;
};

export const prepareUploadedDocs = (uploadedFiles: UploadedFiles | undefined) => {
  if (!uploadedFiles) return [];
  const documents: any[] = [];
  Object.keys(uploadedFiles).forEach((key) => {
    if (uploadedFiles[key]) {
      documents.push({
        id: uploadedFiles[key].id,
        name: uploadedFiles[key].fileName,
        extension: uploadedFiles[key].fileType,
        type: key,
      });
    }
  });
  return documents;
};

export const getActiveAccountByID = (id: string, accounts: AccountProps[]) => {
  return accounts.find((acc: any) => acc.id === id);
};

export const requiredText = (field: RequiredTextProps) => {
  const { elementConfigs } = field;
  const { requiredTxt } = elementConfigs;
  return requiredTxt || "Required";
};

export const getCountryName = (countryCode: string | undefined) => {
  const countriesOption = getOptionsForSelectField("countries");
  const country = countriesOption.find(
    (country: SelectOptionProps) => country.value === countryCode,
  );
  return country?.label ?? countryCode;
};

export const getPurposeOfTranfer = (code: string | undefined) => {
  const option = getOptionsForSelectField("transaction-purposes");
  const value = option.find((country: SelectOptionProps) => country.value === code);
  return value?.label ?? code;
};

export const getStateName = (stateCode: string | undefined) => {
  const statesOption = getOptionsForSelectField("subdivisions");
  const state = statesOption.find((state: SelectOptionProps) => state.value === stateCode);
  return state?.label ?? stateCode;
};

export const checkIfRequiredField = (formFields: any, requiredFieldsKeys: any) => {
  let isValid = true;
  for (const key of requiredFieldsKeys) {
    if (formFields[key] && formFields[key].value) {
      isValid = false;
    }
  }
  return isValid;
};

export const removeAsteriskFromString = (value: string) => {
  return value.replace(" *", "");
};

export const prepareOptions = (options: any): Array<SelectOptionProps> => {
  const preparedOptions: Array<SelectOptionProps> = [];

  for (const key in options) {
    const { name, code } = options[key];
    preparedOptions.push({
      key: code,
      value: code,
      label: name,
    });
  }

  return preparedOptions;
};

export const updateRequiredFields = (
  formFields: any,
  form: any,
  validField: boolean,
  requiredFieldsKeys: string[],
  keyName = "label",
) => {
  if (!formFields) return;
  requiredFieldsKeys.forEach((key) => {
    if (formFields[key]) {
      const newField = removeAsteriskFromString(formFields[key]?.elementConfigs[keyName]);

      formFields[key].elementConfigs = {
        ...formFields[key].elementConfigs,
        validField: validField,
        [keyName]: !validField ? `${newField} *` : newField,
      };
    }
  });
  resetFormFieldValues(formFields, [""], form);
  return formFields;
};

export const UpdateStateByCountry = async (value: any, formFields: any, key: string) => {
  try {
    const { subDivisionsLookup } = await getSubDivisionsLookup(value);
    setLookupsOnStorage([{ ...subDivisionsLookup }]);
    if (subDivisionsLookup?.lookupTable?.length) {
      const stateOptions = prepareOptions(subDivisionsLookup?.lookupTable);
      formFields[key].elementConfigs = {
        ...formFields[key].elementConfigs,
        type: "select",
        options: stateOptions,
      };
    } else {
      formFields[key].elementConfigs = {
        ...formFields[key].elementConfigs,
        type: "string",
        allowSpecialCharacter: false,
      };
    }
    formFields[key].value = undefined;
  } catch (error) {
    showErrorNotification(error);
  }

  return formFields;
};

export const formatNumberWithCommas = (value: string): string => {
  const cleanedValue = value.replace(/[^\d.]/g, "");
  const parts = cleanedValue.split(".");
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return parts.join(".");
};

export const getStatesByCountry = async (countryCode: any) => {
  try {
    const { subDivisionsLookup } = await getSubDivisionsLookup(countryCode);
    return prepareOptions(subDivisionsLookup?.lookupTable);
  } catch (error) {
    showErrorNotification(error);
  }

  return null;
};

export const getAccountTitle = (accountTitle: string, accountNo?: string): string => {
  if (!accountNo || accountNo.length <= 4) {
    return accountTitle;
  }
  const lastFour = accountNo.toString().slice(-4);
  return `${accountTitle} ${padLastFour(lastFour)}`.trim();
};

const getOriginFromReferrer = (): string | null => {
  if (!document.referrer) {
    return null;
  }

  try {
    return new URL(document.referrer).origin;
  } catch {
    return null;
  }
};

export const getPostMessageTargetOrigin = (): string => {
  const ancestorOrigins = (window.location as any).ancestorOrigins;
  if (ancestorOrigins?.length) {
    return ancestorOrigins[0];
  }

  const referrerOrigin = getOriginFromReferrer();
  if (referrerOrigin) {
    return referrerOrigin;
  }

  return "*";
};

export const isEmpty = (value: any): boolean => {
  if (value == null) return true;
  if (typeof value === "string" || Array.isArray(value)) return value.length === 0;
  if (typeof value === "object") return Object.keys(value).length === 0;
  return false;
};

export const round = (num: number, precision = 0): number => {
  const factor = Math.pow(10, precision);
  return Math.round(num * factor) / factor;
};
